/**
 * Single source of truth for reading the Keycloak access token.
 *
 * The token is persisted by Zustand under the "auth-storage" key as JSON -
 * there has never been a bare "token" entry in localStorage. Several services
 * nevertheless called `localStorage.getItem("token")`, got null, and sent
 * `Authorization: Bearer null`, which the backend rejected with
 * "JsonWebTokenError: jwt malformed". That broke GDPR export, consent
 * management, chat and certificate upload for every signed-in user.
 *
 * Prefer the shared `api` client in services/api.ts, which applies this in a
 * request interceptor. Use this helper directly only where a bare
 * fetch call is unavoidable.
 */
export function getAuthToken(): string | null {
  const raw = localStorage.getItem("auth-storage");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return (
      parsed?.state?.token ??
      parsed?.token ??
      parsed?.state?.accessToken ??
      parsed?.accessToken ??
      null
    );
  } catch {
    return null;
  }
}

/**
 * The signed-in user's Keycloak subject, read from the same persisted store.
 *
 * Backend routes authorize by comparing the `:userId` path parameter against
 * `request.user.id`, which `keycloakAuth` sets to the token's `sub` claim. The
 * frontend User carries both a Mongo `id` and a `keycloakId`, so preferring
 * `id` here produces a 403 on every write. Prefer `keycloakId`.
 *
 * unitPreferencesStore previously used `localStorage.getItem("userId")` with a
 * "// Simplified" comment. Nothing writes that key, so the lookup always
 * returned null and every preference update threw "User not authenticated".
 */
export function getAuthUserId(): string | null {
  const raw = localStorage.getItem("auth-storage");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const user = parsed?.state?.user ?? parsed?.user;
    return user?.keycloakId ?? user?.id ?? null;
  } catch {
    return null;
  }
}
